/**
 * Qué conversión va a monitorear cada cuenta que se da de alta, y de dónde salió esa elección.
 *
 * Sólo Meta: es el único canal donde una línea sin evento elegido mide CERO. Google y LinkedIn nacen
 * con «todas las conversiones primarias», que ya cuenta algo, y TikTok no tiene eventos para elegir.
 *
 * La sugerencia NO reusa `looksLikeOutcome` del panel, aunque la primera versión lo hacía. Esa
 * función toma como «resultado» la categoría de compras ENTERA —vista, carrito, checkout,
 * búsqueda— porque sirve para ORDENAR chips. Para ELEGIR sola no alcanza: en el QA del 24/09 las 4
 * cuentas ecommerce sugerían «Contenido visto agrupado» (13.160) por encima de «Compras del píxel»
 * (57), y el alta creó una línea midiendo vistas como conversiones — una línea que da números lindos
 * y no se queja nunca.
 *
 * Acá hay una jerarquía explícita: primero un pago (la compra, o la donación y la cuota en las cuentas
 * que no venden); después lo que deja un contacto (lead,
 * registro, solicitud, cita, suscripción) o una conversión personalizada, que el anunciante definió
 * a propósito; al final la conversación de mensajería. Dentro del nivel gana el volumen. Si la
 * cuenta no registró NADA de eso, no hay sugerencia: la fila queda para que el usuario elija, en vez
 * de fijar sola un evento de actividad.
 *
 * El volumen sale del backend, que se lo pide a Meta en vivo: en el alta no hay métricas
 * sincronizadas.
 */
import {FACEBOOK_CHANNEL, GOOGLE_CHANNEL} from '../channels/channels';
import {getTranslatedName, stripActionPrefix} from '../selects/actionCategories';

export const CONVERSION_SUGGESTED = 'suggested';
export const CONVERSION_EDITED = 'edited';
// «La elijo después» dicho por el usuario. Es distinto de no tener sugerencia: ahí no hubo elección.
export const CONVERSION_LATER = 'later';
export const CONVERSION_NONE = 'none';

// Cómo contestó el backend por cada cuenta. `UNAVAILABLE` es una caída (de Meta o del pedido) y se
// puede reintentar; `NOT_READABLE` es que Dashbo no puede armar el catálogo de la cuenta, y
// reintentar da siempre lo mismo.
export const EVENTS_OK = 'OK';
export const EVENTS_UNAVAILABLE = 'UNAVAILABLE';
export const EVENTS_NOT_READABLE = 'NOT_READABLE';

// Un evento de Meta en sus variantes por origen, tal como las publica el catálogo fijo.
const variantsOf = (event, origins) => origins.map(origin => `${event}_${origin}`);
const ALL_ORIGINS = ['total', 'website', 'mobile_app', 'offline'];

/**
 * Los niveles de la jerarquía, del más fuerte al más débil. Ids sin prefijo.
 *
 * Son ids del catálogo fijo de Meta que emite el backend, y NADA más: un id que el backend no manda
 * nunca llega, y uno del catálogo que no está acá no se sugiere nunca. Las dos cosas las custodia
 * `conversionSuggestion.test.js` contra `__fixtures__/metaFixedActionIds.js` (y el backend avisa si
 * su catálogo cambia).
 */
export const RESULT_TIERS = [
    // 1. Alguien pagó: una compra, una donación o la cuota de una suscripción.
    ['offsite_conversion.fb_pixel_purchase', 'omni_purchase', 'onsite_conversion.purchase',
     'app_custom_event.fb_mobile_purchase',
     ...variantsOf('donate', [...ALL_ORIGINS, 'on_facebook']),
     ...variantsOf('recurring_subscription_payment', ALL_ORIGINS)],
    // 2. Alguien dejó sus datos, pidió algo o se suscribió. Las conversiones personalizadas entran
    //    acá por `is_custom`, no por id.
    ['offsite_conversion.fb_pixel_lead', 'lead', 'leadgen_grouped', 'onsite_conversion.lead_grouped',
     'onsite_conversion.flow_complete',
     'offsite_conversion.fb_pixel_complete_registration', 'omni_complete_registration',
     'app_custom_event.fb_mobile_complete_registration',
     ...variantsOf('submit_application', [...ALL_ORIGINS, 'on_facebook']),
     ...variantsOf('schedule', ALL_ORIGINS),
     ...variantsOf('contact', ALL_ORIGINS),
     ...variantsOf('subscribe', ALL_ORIGINS),
     ...variantsOf('start_trial', ALL_ORIGINS)],
    // 3. Alguien escribió. Es la conversión de muchas cuentas de WhatsApp/Messenger, pero sólo si
    //    no hay nada más firme.
    ['onsite_conversion.messaging_conversation_started_7d', 'onsite_conversion.messaging_first_reply',
     'onsite_conversion.messaging_conversation_replied_7d'],
];

const TIER_BY_ID = {};
RESULT_TIERS.forEach((ids, index) => ids.forEach(id => { TIER_BY_ID[id] = index; }));
const CUSTOM_TIER = 1;

/** El nivel de la jerarquía del evento, o `null` si es actividad (vistas, clics, carrito…). */
export function resultTierOf(action) {
    if (action.is_custom) return CUSTOM_TIER;
    const tier = TIER_BY_ID[stripActionPrefix(action.action_id)];
    return tier === undefined ? null : tier;
}

function volumeOf(action) {
    return Number(action.counts?.last_30_days) || 0;
}

export function needsConversion(account) {
    return account?.channel === FACEBOOK_CHANNEL;
}

/**
 * ¿La cuenta elige conversión con el selector de Google (C-01, contrato §2.1)?
 *
 * Google no lleva sugerencia: sin elegir nada, la línea nace con todas las conversiones primarias,
 * que es lo que cuenta la columna «Conversiones» de Google Ads. Elegir es opcional y se guarda en
 * `conversionEdits[pk]`, igual que Meta; `[]` o sin entrada = todas las primarias.
 */
export function choosesGoogleConversion(account) {
    return account?.channel === GOOGLE_CHANNEL;
}

/** El id de una acción de Google que `add-bulk` acepta: `CUSTOM_ACTION_<dígitos>`. */
const GOOGLE_ACTION_ID = /^CUSTOM_ACTION_\d+$/;

/** Cuántas acciones de Google acepta `add-bulk` por cuenta. */
export const MAX_GOOGLE_ACTIONS = 10;

/**
 * Las acciones de Google elegidas para una cuenta, como viajan en el POST: sólo ids con la forma que
 * acepta el backend, sin repetidos y hasta `MAX_GOOGLE_ACTIONS`. Vacío = todas las primarias.
 */
export function googleActionsFor(pk, conversionEdits) {
    const chosen = conversionEdits?.[pk] || conversionEdits?.[String(pk)] || [];
    return Array.from(new Set(chosen.filter(id => GOOGLE_ACTION_ID.test(String(id)))))
        .slice(0, MAX_GOOGLE_ACTIONS);
}

/** El evento que Dashbo propone para la cuenta, o `null` si no registró ningún resultado. */
export function suggestedActionOf(events) {
    if (!events || events.status !== EVENTS_OK) return null;
    const [first] = orderedResultsOf(events.actions || []);
    return first || null;
}

/**
 * Las opciones del selector, en dos grupos: los resultados —en el mismo orden con que se elige la
 * sugerencia— y el resto por volumen. Ordenar todo por volumen ponía «Interacción con la página»
 * arriba y «Compras del píxel» en el puesto 16 de 32.
 */
export function groupedOptionsOf(actions) {
    const results = orderedResultsOf(actions || []);
    const resultIds = new Set(results.map(action => action.action_id));
    const others = (actions || []).filter(action => !resultIds.has(action.action_id))
                                  .sort((a, b) => volumeOf(b) - volumeOf(a));
    return {results, others};
}

function orderedResultsOf(actions) {
    return actions.filter(action => volumeOf(action) > 0 && resultTierOf(action) !== null)
                  .sort((a, b) => (resultTierOf(a) - resultTierOf(b)) || (volumeOf(b) - volumeOf(a)));
}

/**
 * La conversión de la cuenta: `{actionIds, source}`.
 *
 * Lo que el usuario eligió gana sobre la sugerencia, incluida la elección de dejarla para después
 * (`[]`). Sin elección, vale la sugerencia; sin sugerencia, no hay conversión y la línea nace como
 * nacía antes de este cambio.
 */
export function conversionFor({pk, conversionEvents, conversionEdits}) {
    const edited = conversionEdits?.[pk];
    if (Array.isArray(edited)) {
        return {actionIds: edited, source: edited.length ? CONVERSION_EDITED : CONVERSION_LATER};
    }
    const suggested = suggestedActionOf(conversionEvents?.[String(pk)]);
    if (suggested) return {actionIds: [suggested.action_id], source: CONVERSION_SUGGESTED};
    return {actionIds: [], source: CONVERSION_NONE};
}

/**
 * El nombre del evento en el idioma del usuario. El diccionario del producto está en español; en
 * inglés, la descripción que manda Meta ya es el nombre oficial.
 */
export function conversionLabelOf(action, language = 'es') {
    const inSpanish = !language || language.startsWith('es');
    const translated = inSpanish ? getTranslatedName(action.action_id) : null;
    return translated || action.description || action.name || stripActionPrefix(action.action_id);
}

/** El volumen en formato corto y en el idioma del usuario: «54,9 mil» / «54.9K». */
export function compactVolumeOf(value, language = 'es') {
    const number = Number(value);
    if (!Number.isFinite(number)) return null;
    try {
        return new Intl.NumberFormat(language || 'es', {notation: 'compact', maximumFractionDigits: 1})
            .format(number);
    } catch (error) {
        return String(Math.round(number));
    }
}
